import { getLexiconEntry } from '@tunasong/music-lib'
import { useUserConfig } from '@tunasong/ui-lib'

/**
 * Theory-vocabulary level — gates copy across every music-theory learning
 * surface. Mirrors the enum in `userConfigSchema.musicPreferences.theoryLevel`.
 */
export type TheoryLevel = 'beginner' | 'intermediate' | 'advanced' | 'off'

/**
 * The shape consumed by chips, popups, and suggestion rows. `level` is the
 * resolved level (never `'off'` — when the level is off the hook returns
 * `null`, letting surfaces hide their label entirely).
 *
 * `body` is the level-resolved prose copy (used by `TheoryCard` and the
 * borrowed-line row in `ChordInfoPopup`). `tag` is the level-resolved short
 * chip text (used by `WindowTag`). `displayName` / `displayNameShort` are the
 * editorial names — heading and short-form respectively, level-independent.
 */
export interface TheoryCopy {
  body: string
  tag: string
  level: Exclude<TheoryLevel, 'off'>
  displayName: string
  displayNameShort: string
}

/**
 * Fallback when `userConfig.musicPreferences.theoryLevel` is absent. Per the
 * Phase-1 spec we do not auto-migrate persisted user configs; the default is
 * applied at read time instead.
 */
const DEFAULT_LEVEL: Exclude<TheoryLevel, 'off'> = 'intermediate'

/**
 * Resolve the user's current `theoryLevel` setting (`'off'` included).
 * Surfaces that need to gate on the level itself — not on a specific
 * lexicon copy — call this directly so they don't depend on a lexicon
 * id existing. The sidebar suggestion explainer uses it to decide
 * whether the row is expandable at all.
 */
export const useTheoryLevel = (): TheoryLevel => {
  const [userConfig] = useUserConfig()
  return userConfig?.musicPreferences?.theoryLevel ?? DEFAULT_LEVEL
}

/**
 * Resolve the user's opt-in `showVoiceMotion` toggle (the in-editor
 * voice-leading diff gate). Off by default — the schema fills `false` only when
 * a `musicPreferences` object is present, and we treat an absent object as
 * `false` here too (no auto-migration). Surfaces still gate on `theoryLevel`
 * separately: this hook only answers "did the user opt into voice motion?".
 */
export const useShowVoiceMotion = (): boolean => {
  const [userConfig] = useUserConfig()
  return userConfig?.musicPreferences?.showVoiceMotion === true
}

/**
 * Resolve level-appropriate theory copy for a lexicon id.
 *
 * Returns `null` when:
 *   - the user has set `theoryLevel` to `'off'`, or
 *   - the lexicon does not contain an entry for `lexiconId` (the analyzer may
 *     have emitted an id ahead of its corresponding lexicon entry shipping).
 *
 * Surfaces must treat a `null` return as "render nothing" — no placeholder.
 */
export const useTheoryCopy = (lexiconId: string): TheoryCopy | null => {
  const level = useTheoryLevel()
  if (level === 'off') {
    return null
  }
  const entry = getLexiconEntry(lexiconId)
  if (!entry) {
    return null
  }
  return {
    body: entry.copy[level],
    tag: entry.tagLabel[level],
    level,
    displayName: entry.displayName,
    displayNameShort: entry.displayNameShort,
  }
}
